import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import {
  addWorkstreamsToAnalyticalCheckpoint,
  archiveAnalyticalSubject,
  createAnalyticalCheckpoint,
  createCharacterIndexTaskSet,
  enrollCharacterIndexWitness,
  mergeAnalyticalSubjects,
  migrateAnalyticalTaskSet,
  renameAnalyticalSubject,
  recordCharacterAnalyticalActivity,
  setAnalyticalCheckpointState,
  setAnalyticalTaskSetState,
  setAnalyticalWorkstreamState,
} from '../lib/analyticalTasks'
import type {
  AnalyticalCheckpointScope,
  AnalyticalDimension,
  AnalyticalPosition,
  AnalyticalTaskSet,
  AnalyticalTaskState,
  CharacterIndexTaskSetEnrollment,
} from '../types/analyticalTaskTypes'

const sortTaskSets = (taskSets: readonly AnalyticalTaskSet[]): AnalyticalTaskSet[] => taskSets
  .slice()
  .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id))

const requireTaskSet = (taskSets: Map<string, AnalyticalTaskSet>, taskSetId: string): AnalyticalTaskSet => {
  const taskSet = taskSets.get(taskSetId)
  if (!taskSet) {
    throw new Error(`Analytical task set ${taskSetId} is not loaded.`)
  }
  return taskSet
}

interface AnalyticalTaskStoreState {
  taskSets: Map<string, AnalyticalTaskSet>
  loadingWitnessIds: Set<string>
  loadingAll: boolean
  getForWitness: (witnessId: string) => AnalyticalTaskSet[]
  loadAll: () => Promise<AnalyticalTaskSet[]>
  loadForWitness: (witnessId: string) => Promise<AnalyticalTaskSet[]>
  saveTaskSet: (taskSet: AnalyticalTaskSet) => Promise<void>
  ensureCharacterIndexTaskSet: (params: {
    title: string
    enrollment: CharacterIndexTaskSetEnrollment
    now?: string
  }) => Promise<AnalyticalTaskSet>
  recordCharacterActivity: (params: {
    taskSetId: string
    character: { id: string; label: string; metadata?: Record<string, unknown> }
    chapterId: string
    witnessId: string
    dimension: Extract<AnalyticalDimension, 'identity' | 'coreference' | 'characterization' | 'focalization' | 'speaker' | 'review'>
    position: AnalyticalPosition
    now?: string
    checkpointId?: string
    activeCheckpointId?: string
  }) => Promise<{ taskSet: AnalyticalTaskSet; subjectId: string; workstreamId: string; checkpointId?: string }>
  createCheckpoint: (params: {
    taskSetId: string
    id?: string
    title?: string
    workstreamIds: string[]
    scope: AnalyticalCheckpointScope
    now?: string
  }) => Promise<AnalyticalTaskSet>
  addCheckpointWorkstreams: (params: {
    taskSetId: string
    checkpointId: string
    workstreamIds: string[]
    now?: string
  }) => Promise<AnalyticalTaskSet>
  setWorkstreamState: (params: {
    taskSetId: string
    workstreamId: string
    state: AnalyticalTaskState
    now?: string
  }) => Promise<AnalyticalTaskSet>
  setCheckpointState: (params: {
    taskSetId: string
    checkpointId: string
    state: AnalyticalTaskState
    now?: string
  }) => Promise<AnalyticalTaskSet>
  setTaskSetState: (params: {
    taskSetId: string
    state: AnalyticalTaskState
    now?: string
  }) => Promise<AnalyticalTaskSet>
  mergeCharacterSubjects: (params: {
    taskSetId: string
    sourceCharacterId: string
    receivingCharacterId: string
    now?: string
  }) => Promise<AnalyticalTaskSet>
  archiveCharacterSubject: (params: {
    taskSetId: string
    characterId: string
    now?: string
  }) => Promise<AnalyticalTaskSet>
  renameCharacterSubject: (params: {
    taskSetId: string
    characterId: string
    label: string
    now?: string
  }) => Promise<AnalyticalTaskSet>
}

export const useAnalyticalTaskStore = create<AnalyticalTaskStoreState>((set, get) => ({
  taskSets: new Map(),
  loadingWitnessIds: new Set(),
  loadingAll: false,

  getForWitness: witnessId => sortTaskSets(Array.from(get().taskSets.values())
    .filter(taskSet => taskSet.witnessIds.includes(witnessId))),

  loadAll: async () => {
    if (get().loadingAll) return sortTaskSets(Array.from(get().taskSets.values()))
    set({ loadingAll: true })
    try {
      const persisted = await dbStorage.getAll('analytical_task_sets')
      const migrations = persisted.map(taskSet => ({
        previousVersion: taskSet.schemaVersion,
        taskSet: migrateAnalyticalTaskSet(taskSet),
      }))
      const taskSets = sortTaskSets(migrations.map(migration => migration.taskSet))
      await Promise.all(migrations.flatMap(migration => (
        migration.previousVersion === migration.taskSet.schemaVersion
          ? []
          : [dbStorage.put('analytical_task_sets', migration.taskSet)]
      )))
      set(state => {
        const next = new Map(state.taskSets)
        taskSets.forEach(taskSet => next.set(taskSet.id, taskSet))
        return { taskSets: next }
      })
      return taskSets
    } finally {
      set({ loadingAll: false })
    }
  },

  loadForWitness: async witnessId => {
    const cached = get().getForWitness(witnessId)
    if (cached.length > 0) return cached
    set(state => ({ loadingWitnessIds: new Set(state.loadingWitnessIds).add(witnessId) }))
    try {
      const persisted = await dbStorage.getAllByIndex(
        'analytical_task_sets', 'witnessIds', witnessId,
      )
      const migrations = persisted.map(taskSet => ({
        previousVersion: taskSet.schemaVersion,
        taskSet: migrateAnalyticalTaskSet(taskSet),
      }))
      const taskSets = sortTaskSets(migrations.map(migration => migration.taskSet))
      await Promise.all(migrations.flatMap(migration => (
        migration.previousVersion === migration.taskSet.schemaVersion
          ? []
          : [dbStorage.put('analytical_task_sets', migration.taskSet)]
      )))
      set(state => {
        const next = new Map(state.taskSets)
        taskSets.forEach(taskSet => next.set(taskSet.id, taskSet))
        return { taskSets: next }
      })
      return taskSets
    } finally {
      set(state => {
        const loadingWitnessIds = new Set(state.loadingWitnessIds)
        loadingWitnessIds.delete(witnessId)
        return { loadingWitnessIds }
      })
    }
  },

  saveTaskSet: async taskSet => {
    const normalized = migrateAnalyticalTaskSet(taskSet)
    await dbStorage.put('analytical_task_sets', normalized)
    set(state => ({ taskSets: new Map(state.taskSets).set(normalized.id, normalized) }))
  },

  ensureCharacterIndexTaskSet: async ({ title, enrollment, now }) => {
    const id = `character-index:${enrollment.workId}`
    const cached = get().taskSets.get(id)
    const persisted = cached || await dbStorage.get('analytical_task_sets', id)
    const taskSet = persisted
      ? enrollCharacterIndexWitness({ taskSet: migrateAnalyticalTaskSet(persisted), enrollment, now })
      : createCharacterIndexTaskSet({ id, title, enrollments: [enrollment], now })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  recordCharacterActivity: async params => {
    const taskSet = requireTaskSet(get().taskSets, params.taskSetId)
    const result = recordCharacterAnalyticalActivity({
      taskSet,
      character: params.character,
      chapterId: params.chapterId,
      witnessId: params.witnessId,
      dimension: params.dimension,
      position: params.position,
      now: params.now,
      checkpointId: params.checkpointId,
      activeCheckpointId: params.activeCheckpointId,
    })
    await get().saveTaskSet(result.taskSet)
    return result
  },

  createCheckpoint: async ({ taskSetId, ...params }) => {
    const taskSet = createAnalyticalCheckpoint({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      ...params,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  addCheckpointWorkstreams: async ({ taskSetId, ...params }) => {
    const taskSet = addWorkstreamsToAnalyticalCheckpoint({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      ...params,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  setWorkstreamState: async ({ taskSetId, ...params }) => {
    const taskSet = setAnalyticalWorkstreamState({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      ...params,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  setCheckpointState: async ({ taskSetId, ...params }) => {
    const taskSet = setAnalyticalCheckpointState({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      ...params,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  setTaskSetState: async ({ taskSetId, ...params }) => {
    const taskSet = setAnalyticalTaskSetState({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      ...params,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  mergeCharacterSubjects: async ({ taskSetId, sourceCharacterId, receivingCharacterId, now }) => {
    const taskSet = mergeAnalyticalSubjects({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      sourceSubjectId: `character:${sourceCharacterId}`,
      receivingSubjectId: `character:${receivingCharacterId}`,
      now,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  archiveCharacterSubject: async ({ taskSetId, characterId, now }) => {
    const taskSet = archiveAnalyticalSubject({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      subjectId: `character:${characterId}`,
      now,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },

  renameCharacterSubject: async ({ taskSetId, characterId, label, now }) => {
    const taskSet = renameAnalyticalSubject({
      taskSet: requireTaskSet(get().taskSets, taskSetId),
      subjectId: `character:${characterId}`,
      label,
      now,
    })
    await get().saveTaskSet(taskSet)
    return taskSet
  },
}))
